import React, { useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ExchangeRateProvider } from "./context/ExchangeRateContext";
import { ModalProvider } from "./context/ModalContext";
import { SettingsProvider } from "./context/SettingsContext";
import { PermissionProvider, usePermissions } from "./context/PermissionContext";
import RoleManagement from "./pages/RoleManagement";
import ModelistMyOrders from "./pages/ModelistMyOrders";
import AccountingOrders from "./pages/AccountingOrders";
import Documents from "./pages/Documents";
import IncomingInvoices from "./pages/IncomingInvoices";
import Receivables from "./pages/Receivables";
import FirmStatement from "./pages/FirmStatement";
import Purchasing from "./pages/Purchasing";
import PurchaseOrderForm from "./pages/PurchaseOrderForm";
import Shipping from "./pages/Shipping";
import ShipmentForm from "./pages/ShipmentForm";
import Appointments from "./pages/Appointments";
import Hr from "./pages/Hr";
import Kiosk from "./pages/Kiosk";
import {
  BookingDirectory,
  BookingForm,
  BookingVerify,
  BookingRecord,
} from "./pages/PublicBooking";
import Notifications from "./pages/Notifications";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ChangePassword from "./pages/ChangePassword";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import UserManagement from "./pages/UserManagement";
import ExternalTickets from "./pages/ExternalTickets";
import Orders from "./pages/Orders";
import OrderForm from "./pages/OrderForm";
import OrderDetail from "./pages/OrderDetail";
import OrderQRDetail from "./pages/OrderQRDetail";
import WorkshopKanban from "./pages/WorkshopKanban";
import Firms from "./pages/Firms";
import Models from "./pages/Models";
import ModelCosts from "./pages/ModelCosts";
import Workshops from "./pages/Workshops";
import Operators from "./pages/Operators";
import Technics from "./pages/Technics";
import Reports from "./pages/Reports";
import DynamicReports from "./pages/DynamicReports";
import Settings from "./pages/Settings";
import CostManagement from "./pages/CostManagement";
import CategoryForm from "./pages/CategoryForm";
import CostItemForm from "./pages/CostItemForm";
import StockManagement from "./pages/StockManagement";
import ProductTree from "./pages/ProductTree";
import Suppliers from "./pages/Suppliers";
import Cash from "./pages/Cash";
import Payables from "./pages/Payables";
import Cheques from "./pages/Cheques";
import TrialBalance from "./pages/TrialBalance";
import FinanceReports from "./pages/FinanceReports";
import Sidebar from "./components/Sidebar";
import Header from "./components/Header";
import PageLoader from "./components/PageLoader";
import "./App.css";

// İzin-tabanlı ana sayfa yönlendirmesi — erişilebilen ilk ekrana götürür
const HomeRedirect: React.FC = () => {
  const { hasScreen, loaded } = usePermissions();
  if (!loaded) return <PageLoader />;
  const priority = [
    "orders", "modelist-my-orders", "accounting-orders", "workshop-kanban",
    "models", "firms", "cost", "model-costs", "reports", "dynamic-reports",
    "stock-management", "user-management", "roles", "settings",
  ];
  const first = priority.find((s) => hasScreen(s));
  return <Navigate to={first ? `/${first}` : "/change-password"} replace />;
};

const PrivateRoute: React.FC<{ children: React.ReactElement }> = ({
  children,
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading while checking authentication
  if (isLoading) {
    return <PageLoader message="Oturum doğrulanıyor..." />;
  }

  if (!isAuthenticated) {
    // Kullanıcının gitmek istediği URL'i hem state'e hem sessionStorage'a kaydet
    const redirectPath = `${location.pathname}${location.search}`;
    sessionStorage.setItem("redirectAfterLogin", redirectPath);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Ekran-yol eşlemesi (dinamik izin kapılaması için)
const SCREEN_BY_PATH: [string, string][] = [
  ["/accounting-orders", "accounting-orders"],
  ["/documents", "e-documents"],
  ["/incoming-invoices", "incoming-invoices"],
  ["/receivables", "receivables"],
  ["/cash", "cash"],
  ["/payables", "payables"],
  ["/cheques", "cheques"],
  ["/trial-balance", "trial-balance"],
  ["/finance-reports", "dynamic-reports"],
  ["/purchasing", "purchasing"],
  ["/shipping", "shipping"],
  ["/hr", "hr"],
  ["/modelist-my-orders", "modelist-my-orders"],
  ["/user-management", "user-management"],
  ["/register", "user-management"],
  ["/roles", "roles"],
  ["/tickets", "external-tickets"],
  ["/firms", "firms"],
  ["/model-costs", "model-costs"],
  ["/models", "models"],
  ["/workshops", "workshops"],
  ["/operators", "workshops"],
  ["/technics", "workshops"],
  ["/cost", "cost"],
  ["/reports", "reports"],
  ["/dynamic-reports", "dynamic-reports"],
  ["/settings", "settings"],
  ["/stock-management", "stock-management"],
  ["/product-tree", "product-tree"],
  ["/suppliers", "suppliers"],
  ["/workshop-kanban", "workshop-kanban"],
  ["/orders", "orders"],
];
const screenForPath = (path: string): string | undefined =>
  SCREEN_BY_PATH.find(([p]) => path === p || path.startsWith(p + "/"))?.[1];

// Dinamik izin-tabanlı route koruması — statik rol guard'larının yerine.
// İzin yoksa (sistem henüz kurulmadıysa) permissive davranır (PermissionContext).
const PermissionRoute: React.FC<{ children: React.ReactElement }> = ({
  children,
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { hasScreen, loaded } = usePermissions();
  const location = useLocation();

  if (isLoading) {
    return <PageLoader message="Oturum doğrulanıyor..." />;
  }
  if (!isAuthenticated) {
    sessionStorage.setItem(
      "redirectAfterLogin",
      `${location.pathname}${location.search}`,
    );
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const screen = screenForPath(location.pathname);
  if (screen && loaded && !hasScreen(screen)) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Layout component with sidebar
const Layout: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="app-layout">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Main content area */}
      <div className="main-content">
        {/* Header Component with menu button */}
        <div className="header-container">
          <Header onMenuClick={() => setSidebarOpen(true)} />
        </div>

        <div className="content-container">{children}</div>
      </div>
    </div>
  );
};

// Full width layout for Kanban (no content-container wrapper)
const FullWidthLayout: React.FC<{ children: React.ReactElement }> = ({
  children,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="app-layout">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Main content area - full width */}
      <div className="main-content" style={{ overflow: "visible" }}>
        {/* Header Component with menu button */}
        <div className="header-container">
          <Header onMenuClick={() => setSidebarOpen(true)} />
        </div>

        {children}
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <ExchangeRateProvider>
          <ModalProvider>
            <PermissionProvider>
              <BrowserRouter>
              <Routes>
                <Route
                  path="/modelist-my-orders"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <ModelistMyOrders />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/accounting-orders"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <AccountingOrders />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/documents"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <Documents />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/incoming-invoices"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <IncomingInvoices />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/receivables"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <Receivables />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/receivables/firm/:firmId"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <FirmStatement />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                <Route path="/purchasing" element={<PermissionRoute><Layout><Purchasing /></Layout></PermissionRoute>} />
                <Route path="/purchasing/new" element={<PermissionRoute><Layout><PurchaseOrderForm /></Layout></PermissionRoute>} />
                <Route path="/purchasing/:id" element={<PermissionRoute><Layout><PurchaseOrderForm /></Layout></PermissionRoute>} />
                <Route path="/shipping" element={<PermissionRoute><Layout><Shipping /></Layout></PermissionRoute>} />
                <Route path="/shipping/new" element={<PermissionRoute><Layout><ShipmentForm /></Layout></PermissionRoute>} />
                <Route path="/shipping/:id" element={<PermissionRoute><Layout><ShipmentForm /></Layout></PermissionRoute>} />
                <Route path="/appointments" element={<PermissionRoute><Layout><Appointments /></Layout></PermissionRoute>} />
                <Route path="/hr" element={<PermissionRoute><Layout><Hr /></Layout></PermissionRoute>} />
                <Route path="/notifications" element={<PrivateRoute><Layout><Notifications /></Layout></PrivateRoute>} />

                {/* Girişsiz randevu sayfaları: oturum, menü ve yetki YOK — dışarıdaki
                    kişi bu adresleri doğrudan açar. Sabit yollar :slug'dan ÖNCE gelmeli. */}
                <Route path="/kiosk" element={<Kiosk />} />
                <Route path="/randevu" element={<BookingDirectory />} />
                <Route path="/randevu/dogrula/:token" element={<BookingVerify />} />
                <Route path="/randevu/kayit/:token" element={<BookingRecord />} />
                <Route path="/randevu/:slug" element={<BookingForm />} />

                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/change-password"
                  element={
                    <PrivateRoute>
                      <ChangePassword />
                    </PrivateRoute>
                  }
                />

                {/* QR Code Route - Authentication required for redirect to work */}
                <Route
                  path="/orderdetail/:orderId"
                  element={
                    <PrivateRoute>
                      <OrderQRDetail />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/register"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <Register />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/user-management"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <UserManagement />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/tickets"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <ExternalTickets />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/orders"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <Orders />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/orders/new"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <OrderForm />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/orders/edit/:orderId"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <OrderForm />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/orders/detail/:orderId"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <OrderDetail />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/workshop-kanban"
                  element={
                    <PrivateRoute>
                      <FullWidthLayout>
                        <WorkshopKanban />
                      </FullWidthLayout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/firms"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <Firms />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/models"
                  element={
                    <PrivateRoute>
                      <Layout>
                        <Models />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/workshops"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <Workshops />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/models"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <Models />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                {/* Model Costs - Firma Listesi */}
                <Route
                  path="/model-costs"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <ModelCosts />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                {/* Model Costs - Firma Modelleri */}
                <Route
                  path="/model-costs/firm/:firmId"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <ModelCosts />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                {/* Model Costs - Model Detay */}
                <Route
                  path="/model-costs/model/:modelId"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <ModelCosts />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                {/* Model Costs - Belirli Sipariş */}
                <Route
                  path="/model-costs/model/:modelId/order/:orderId"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <ModelCosts />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                {/* Eski route - geriye uyumluluk için */}
                <Route
                  path="/models/:modelId/costs"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <ModelCosts />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/operators"
                  element={
                    <PrivateRoute>
                      <PermissionRoute>
                        <Layout>
                          <Operators />
                        </Layout>
                      </PermissionRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/technics"
                  element={
                    <PrivateRoute>
                      <PermissionRoute>
                        <Layout>
                          <Technics />
                        </Layout>
                      </PermissionRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <PrivateRoute>
                      <PermissionRoute>
                        <Layout>
                          <Reports />
                        </Layout>
                      </PermissionRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/dynamic-reports"
                  element={
                    <PrivateRoute>
                      <PermissionRoute>
                        <Layout>
                          <DynamicReports />
                        </Layout>
                      </PermissionRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <Settings />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/cost"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <CostManagement />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/cost/categories/new"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <CategoryForm />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/cost/categories/:id"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <CategoryForm />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/cost/items/new"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <CostItemForm />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/cost/items/:id/edit"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <CostItemForm />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/stock-management"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <StockManagement />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/product-tree"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <ProductTree />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                <Route path="/suppliers" element={<PermissionRoute><Layout><Suppliers /></Layout></PermissionRoute>} />
                <Route path="/cash" element={<PermissionRoute><Layout><Cash /></Layout></PermissionRoute>} />
                <Route path="/payables" element={<PermissionRoute><Layout><Payables /></Layout></PermissionRoute>} />
                <Route path="/cheques" element={<PermissionRoute><Layout><Cheques /></Layout></PermissionRoute>} />
                <Route path="/trial-balance" element={<PermissionRoute><Layout><TrialBalance /></Layout></PermissionRoute>} />
                <Route path="/finance-reports" element={<PermissionRoute><Layout><FinanceReports /></Layout></PermissionRoute>} />
                <Route
                  path="/roles"
                  element={
                    <PermissionRoute>
                      <Layout>
                        <RoleManagement />
                      </Layout>
                    </PermissionRoute>
                  }
                />
                <Route
                  path="/"
                  element={
                    <PrivateRoute>
                      <HomeRedirect />
                    </PrivateRoute>
                  }
                />
              </Routes>
              </BrowserRouter>
            </PermissionProvider>
          </ModalProvider>
        </ExchangeRateProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;
